<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content>
        <div class="tw-flex tw-justify-between tw-items-center">
          <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Settings</h1>
          <base-button v-if="organization.operatingModel === 'direct'" @click="openSignupLinks" :disabled="!organization.isActive" type="primary"
            >{{ memberSignupLinksTitle }}
            <template v-slot:icon>
              <qr-icon /> </template
          ></base-button>
        </div>
      </base-content>
    </div>
    <base-content>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-y-6 tw-gap-x-4">
        <div class="tw-flex" v-for="link in managementLinks" :key="link.route">
          <div class="tw-h-8 tw-w-8 tw-p-1.5 tw-flex tw-flex-shrink-0 tw-bg-primary-50 tw-border tw-border-primary-200 tw-rounded-full tw-items-center tw-justify-center">
            <component :is="link.icon" class="tw-h-6 tw-w-6 tw-text-primary-500 tw-align-middle" />
          </div>
          <div class="tw-ml-2">
            <base-button type="text" size="lg" @click="linkClick(link)">{{ link.label }}</base-button>
            <div class="tw-text-sm">{{ link.description }}</div>
          </div>
        </div>
        <div class="tw-flex" v-for="link in adminLinks" :key="link.route">
          <div class="tw-h-8 tw-w-8 tw-p-1.5 tw-flex tw-flex-shrink-0 tw-bg-primary-50 tw-border tw-border-primary-200 tw-rounded-full tw-items-center tw-justify-center">
            <component :is="link.icon" class="tw-h-6 tw-w-6 tw-text-primary-500 tw-align-middle" />
          </div>
          <div class="tw-ml-2">
            <base-button type="text" size="lg" @click="linkClick(link)">{{ link.label }}</base-button>
            <div class="tw-text-sm">{{ link.description }}</div>
          </div>
        </div>
      </div>
    </base-content>
    <signup-links ref="signupLinksModal" :businessId="businessId" :operatingModel="organization.operatingModel" />
  </div>
</template>

<script>
import SignupLinks from "@/views/business/settings/components/SignupLinks";
import { QrIcon, SettingsIcon, UsersIcon, LocationIcon, CheckIcon, CreditCardIcon, ListIcon, BusinessIcon, MoneyIcon, CardReaderIcon, ArrowRenewIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import { capitalize } from "lodash";

export default {
  name: "BusinessSettingsView",
  components: {
    SignupLinks,
    QrIcon,
    SettingsIcon,
    UsersIcon,
    LocationIcon,
    CheckIcon,
    CreditCardIcon,
    ListIcon,
    BusinessIcon,
    MoneyIcon,
    CardReaderIcon,
    ArrowRenewIcon,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      business: {},
    };
  },
  computed: {
    ...mapGetters("businessActivation", [
      "hasBusinessProfile",
      "hasTaxRates",
      "hasLocation",
      "hasSubscription",
      "hasProductsAndPlans",
      "hasBrandColorsAndLogo",
      "hasMerchantProcessing",
    ]),
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("role", ["roles", "isPredictile", "isBusinessAdministrator", "isBusinessManagement", "isBusinessOperations", "isLocationFiltered"]),
    showAdminSettings() {
      if (this.isPredictile() === true || this.isBusinessAdministrator(this.businessId) === true)
        if (this.isLocationFiltered === false) return true;
        else return false;
      else return false;
    },
    showManagementSettings() {
      if (this.isPredictile() === true || this.isBusinessManagement(this.businessId) === true) return true;
      else return false;
    },
    managementLinks() {
      if (this.showManagementSettings === false) return [];
      var links = [];

      if (this.isLocationFiltered === false && this.organization.useCreditsSetting === true) {
        links.push({
          icon: "SettingsIcon",
          label: "Redeem Credits Settings",
          route: "settings",
          description: `Customize the way your ${this.organization.customerLabelPlural} can redeem credits to fit your business needs. Set redemption maximums, allow ${
            this.organization.customerLabelPlural
          } to leave tips, and more.`,
        });
      }
      if (this.organization.useTodoSetting === true && !(this.isLocationFiltered && this.organization.operatingModel === "coordinator")) {
        links.push({
          icon: "CheckIcon",
          label: "Manage Activity Types",
          route: "activitytypes",
          description: "Add customized activity types for your team.",
        });
      }

      links.push({
        icon: "LocationIcon",
        label: "Manage Locations",
        component: "BusinessLocations",
        route: "locations",
        description: "Manage and configure your business locations.",
      });

      if (!this.isLocationFiltered) {
        links.push({
          icon: "UsersIcon",
          label: "Manage Team Members",
          route: "users/users",
          description: "Invite new team members and manage the access levels of your existing team members.",
        });
      }
      links.push({
        icon: "CardReaderIcon",
        label: "Manage Credit Card Readers",
        route: "cardreaders",
        description: "If you purchased card readers, register and manage them here.",
      });

      return links;
    },
    adminLinks() {
      if (this.showAdminSettings === false) return [];

      return [
        {
          icon: "ListIcon",
          label: "Manage Business Profile",
          route: "profile",
          description: "Configure the core details of your business such as name, contact information, and business type.",
        },
        {
          icon: "BusinessIcon",
          label: "Customize Your Brand",
          route: "brand",
          description: `Setup your logo and color pallete so that ${this.organization.customerLabelSingular} emails and signup screens reflect your unique brand.`,
        },
        {
          icon: "MoneyIcon",
          label: "Manage Sales Tax Rates",
          route: "taxrates",
          description: "Selling something that requires sales tax? No problem. Setup your tax rates in all the locaions in which you do business.",
        },
        {
          icon: "ArrowRenewIcon",
          label: "Manage Predictile Subscription",
          component: "BusinessSubscription",
          route: "subscription/details",
          description: "Setup your subscription to the Predictile platform.",
        },
        {
          icon: "CreditCardIcon",
          label: "Connect Merchant Account",
          route: "merchant",
          description: `Connect Predictile to your merchant account (Stripe). This allows Predictile to charge and store ${
            this.organization.customerLabelSingular
          } credit cards and deposit directly to your bank account.`,
        },
      ];
    },
    memberSignupLinksTitle() {
      return `${capitalize(this.organization.customerLabelSingular)} Signup Links`;
    },
  },
  methods: {
    href(path) {
      this.$router.push({
        path: path,
        params: { businessId: this.businessId },
      });
    },
    openSignupLinks() {
      this.$refs.signupLinksModal.show();
    },
    linkClick(link) {
      this.href("/businesses/" + this.businessId + "/business/" + link.route);
    },
    showRequired(link) {
      if (!this.organization.isActive) {
        if (link.route === "locations" && !this.hasLocation) return true;
        else if (link.route === "profile" && !this.hasBusinessProfile) return true;
        else if (link.route === "brand" && !this.hasBrandColorsAndLogo) return true;
        else if (link.route === "subscription" && !this.hasSubscription) return true;
        else if (link.route === "merchant" && !this.hasMerchantProcessing) return true;
        else return false;
      } else return false;
    },
  },
};
</script>
